<script lang="ts">
  import { Hand } from "lucide-svelte"

  interface Props {
    section: StatementSectionType
  }
  let props = $props()
  let { section } = props

  import { sections } from "$app/data"

  const sectionD = sections[section]

  import brand_purpose from "@images/onboarding/brand_purpose_example.jpg"
  import values from "@images/onboarding/values_example.jpg"
  import blue_sky_vision from "@images/onboarding/blue_sky_vision_example.jpg"
  import strengths from "@images/onboarding/strengths_example.jpg"
  import weaknesses from "@images/onboarding/weaknesses_example.jpg"
  import opportunities from "@images/onboarding/opportunities_example.jpg"
  import threats from "@images/onboarding/threats_example.jpg"
</script>

<div class="mt-6 bg-lwp-blue py-8 px-12 rounded shadow">
  {#if section === "BrandPurpose"}
    <p
      class="text-lg italic font-bold text-gray-200 border-b border-blue-50 pb-6"
    >
      You have not yet created your Brand Purpose. Please read below for details
      on how to craft your Brand Purpose with your team. Once you are ready you
      can get started by clicking the "New" button above.
    </p>

    <h2 class="mt-8 text-2xl font-bold text-left text-white">
      What Is Your Brand Purpose?
    </h2>

    <p class="mt-8 text-white text-lg">
      The reason your company exists and the difference your products and
      services make in the world. It is sometimes referred to as your mission
      statement. It should be relevant 50 years in the future.
    </p>

    <p class="mt-8 text-white text-lg">
      A Brand Purpose Headline should be less than 5 words and you will also
      choose a symbol to accompany your headline which dramatically increases
      employee retention and engagement. You can optionally add an extended
      description.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Brand Purpose Example</p>
    <img
      src={brand_purpose}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Brand Purpose"
    />
  {:else if section === "CoreValue"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created your Values. Please read below for details on how
      to craft your Values with your team. Once you are ready you can get
      started creating your first value by clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Are Your Values?
    </h2>

    <p class="mt-8 text-white text-lg max-w-lg">
      These are the principles you want your company to live by. We recommend
      entering your top 5 values.
    </p>

    <p class="mt-8 text-white text-lg">
      We recommend that each value headline be 1 or 2 words and a symbol
      assigned to it. In total we recommend a maximum of 5 values for your plan.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Values Example</p>
    <img
      src={values}
      class="mx-auto w-full shadow rounded border border-gray-200 mt-2"
      alt="Values"
    />
  {:else if section === "BlueSkyVision"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created your Blue Sky Vision. Please read below for
      details on how to craft your Blue Sky Vision with your team. Once you are
      ready you can get started by clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Is Your Blue Sky Vision?
    </h2>

    <p class="mt-8 text-white text-lg max-w-lg">
      This is your 5-Year dream of what you want to achieve. It is typically the
      number of people your product and services have impacted.
    </p>

    <p class="mt-8 text-white text-lg">
      We recommend that your Blue Sky Vision be less than 5 words and ensure a
      symbol has been assigned to it.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Blue Sky Vision Example</p>
    <img
      src={blue_sky_vision}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Blue Sky Vision"
    />
  {:else if section === "Strength"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created any Strengths. Please read below for details on
      how to determine your strengths. Once you are ready you can get started by
      clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Are Your Strengths?
    </h2>
    <p class="mt-8 text-white text-lg">
      A strength is any internal talent, skills, competency, technology,
      individual, or relationship that brings your company competetive
      advantage. You can control these and they give you a distinct advantage.
    </p>
    <p class="mt-8 text-white text-lg">
      We recommend that you add 2 - 3 strengths.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Example of Strengths</p>
    <img
      src={strengths}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Strengths"
    />
  {:else if section === "Weakness"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created any Weaknesses. Please read below for details on
      how to determine your weaknesses. Once you are ready you can get started
      by clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Are Your Weaknesses?
    </h2>
    <p class="mt-8 text-white text-lg">
      A weakness is the opposite of a strength - talent, resources,
      competencies, relationships, individuals, processes, technologies and so
      on - that you lack internally and does not support your competitive
      advantage.
    </p>
    <p class="mt-8 text-white text-lg">
      We recommend that you add 2 - 3 weaknesses.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Example of Weaknesses</p>
    <img
      src={weaknesses}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Weaknesses"
    />
  {:else if section === "Opportunity"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created any Opportunities. Please read below for details
      on how to determine your opportunities. Once you are ready you can get
      started by clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Are Your Opportunities?
    </h2>
    <p class="mt-8 text-white text-lg">
      Assessing your opportunities is largely an external facing exercise. You
      want to analyze what conditions in the outside world might allow you to
      exploit your strenghts or make up for your weaknesses.
    </p>
    <p class="mt-8 text-white text-lg">
      We recommend that you add 2 - 3 opportunities.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">
      Example of Opportunities
    </p>
    <img
      src={opportunities}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Opportunities"
    />
  {:else if section === "Threat"}
    <p class="text-xl font-bold text-gray-200 border-b border-blue-50 pb-6">
      You have not yet created any Threats. Please read below for details on how
      to determine your threats. Once you are ready you can get started by
      clicking the "New" button above.
    </p>

    <h2 class="mt-12 text-2xl font-bold text-left text-white">
      What Are Your Threats?
    </h2>
    <p class="mt-8 text-white text-lg">
      A threat is an external change in economic conditions, markets,
      competition, or social trends that worsens your position.
    </p>
    <p class="mt-8 text-white text-lg">
      We recommend that you add 2 - 3 threats.
    </p>

    <p class="mt-6 text-gray-200 italic text-center">Example of Threats</p>
    <img
      src={threats}
      class="mx-auto w-64 shadow rounded border border-gray-200 mt-2"
      alt="Threats"
    />
  {/if}

  <p class="mt-8 text-gray-100 text-base italic">
    We are here for you, if you need help building out your plan, click the
    button below to schedule a meeting with our team.
  </p>

  <div class="mt-6 flex justify-center">
    <div>
      <a
        href="https://calendly.com/anjalwp/lead-with-purpose-discovery-new-sign-up"
        target="_blank"
        class="text-gray-200 border rounded-sm border-white px-6 hover:bg-indigo-600 hover:border-gray-100 hover:font-bold h-14 flex items-center"
      >
        <Hand class="text-gray-200 size-6 mr-2" />
        I need help
      </a>
    </div>
  </div>
</div>
